import React, { useState } from 'react';
import { Button, Card } from '@ygtechnologies/rubus-ui';
import { faArchive, faBox, faCheck, faCircleInfo, faGripDotsVertical, faMinus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { classnames, InputWrapper, Tooltip, ValidatingInput, toNullableNumber } from '@ygtechnologies/bygr-frontend-component-library';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import theme from './SelectedProductCard.module.scss';
import { t } from '@lingui/core/macro';
import { useGetActiveProject } from '@/utils/hooks';
import { ProductSourceCatalog, ProjectFeeSet } from '@/types';
import { FeePreviewCompact } from '@/seller/components/fees';
import { ProductMoreInfoModal } from '@/components/OptionConfigurator/ProductMoreInfoModal/ProductMoreInfoModal';
import { CatalogProductThumbnails } from '@/seller/components/products/ProductThumbnails';
import { OptionGroupProductsEditItem } from './OptionGroupProductsEdit.types';

interface SelectedProductCardProps {
    product: OptionGroupProductsEditItem;
    onRemove(item: OptionGroupProductsEditItem): void;
    onSetDefault(item: OptionGroupProductsEditItem): void;
    onPriceChange(item: OptionGroupProductsEditItem, newPrice: number): void;
    onArchiveChange(item: OptionGroupProductsEditItem, archived: boolean): void;
    dragHandleProps: object;
    selectedFeeSet: ProjectFeeSet | null;
}

export const SelectedProductCard: React.FC<SelectedProductCardProps> = ({
    product,
    onRemove,
    onSetDefault,
    onPriceChange,
    onArchiveChange,
    dragHandleProps,
    selectedFeeSet,
}) => {
    const project = useGetActiveProject();
    const projectCurrencySymbol = project?.currencyCode ?? 'NOK';
    const [showMoreInfoModal, setShowMoreInfoModal] = useState(false);
    return (
        <Card className={classnames(theme.selectedProductCard, { [theme.archived]: product.isArchived })}>
            <div className={theme.dragHandle} {...dragHandleProps}>
                <FontAwesomeIcon icon={faGripDotsVertical} size="xl" fixedWidth />
            </div>
            <div className={theme.productInfoContainer}>
                <CatalogProductThumbnails
                    catalog={ProductSourceCatalog.Project}
                    images={product.product.images || []}
                    className={theme.productImage}
                />

                <div className={theme.productInfo}>
                    <div className={theme.topSection}>
                        <div className={theme.titleAndDescription}>
                            <span className={theme.title}>
                                <Trans>{product.product.name}</Trans>
                            </span>
                        </div>
                        <Button
                            icon={faCircleInfo}
                            variant="tertiary"
                            onClick={(e) => setShowMoreInfoModal(true)}
                            size="sm">
                            <Trans>Product info</Trans>
                        </Button>
                    </div>

                    <div className={theme.pricingCard}>
                        <div className={theme.pricingCardItem}>
                            <span className={theme.pricingCardItemLabel}>
                                <Trans>Sales price</Trans>
                                <Tooltip content={<Trans>Suggested price: {product.product.defaultPrice}</Trans>}>
                                    <FontAwesomeIcon icon={faInfoCircle} />
                                </Tooltip>
                            </span>
                            <div>
                                <InputWrapper appendContent={projectCurrencySymbol}>
                                    <ValidatingInput
                                        className="form-control"
                                        type="number"
                                        placeholder={t`Price`}
                                        value={product.price.toString()}
                                        onChange={(e) => onPriceChange(product, toNullableNumber(e.target.value) ?? 0)}
                                    />
                                </InputWrapper>
                            </div>
                        </div>

                        <div
                            className={theme.pricingCardItem}
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                gap: '4px',
                                width: '100%',
                            }}>
                            <span className={theme.pricingCardItemLabel}>
                                <Trans>Price incl. fees and VAT</Trans>
                            </span>

                            <span className={theme.feePreview}>
                                <FeePreviewCompact
                                    price={product.price || 0}
                                    quantity={1}
                                    projectFeeSet={selectedFeeSet}
                                    className={theme.feePrice}
                                />
                            </span>
                        </div>
                    </div>

                    <div className={theme.removeButtonContainer}>
                        {!product.isArchived && (
                            <Button
                                icon={faCheck}
                                variant={product.isDefault ? 'primary' : 'tertiary'}
                                onClick={(e) => onSetDefault(product)}
                                size="sm">
                                {product.isDefault ? <Trans>Default</Trans> : <Trans>Set as default</Trans>}
                            </Button>
                        )}
                        {product.isDeletable && (
                            <Button icon={faMinus} variant="tertiary" onClick={(e) => onRemove(product)} size="sm">
                                <Trans>Remove</Trans>
                            </Button>
                        )}

                        {!product.isArchived && (
                            <Button
                                variant="tertiary"
                                onClick={(e) => onArchiveChange(product, true)}
                                size="sm"
                                icon={faArchive}
                                disabled={product.isDefault}>
                                <Trans>Archive</Trans>
                            </Button>
                        )}

                        {product.isArchived && (
                            <Button
                                variant="tertiary"
                                onClick={(e) => onArchiveChange(product, false)}
                                size="sm"
                                icon={faArchive}>
                                <Trans>Unarchive</Trans>
                            </Button>
                        )}
                    </div>
                </div>
            </div>
            <ProductMoreInfoModal
                isOpen={showMoreInfoModal}
                product={product.product}
                toggleModal={() => setShowMoreInfoModal(false)}
            />
        </Card>
    );
};
